import { useEffect, type RefObject } from "react";
import type { ScopedThreadRef } from "@amoreg/contracts";
import type { ChatComposerHandle } from "../components/chat/ChatComposer";
import {
  isSameSidebarThreadRef,
  useSidebarPendingFileDropStore,
} from "../sidebarPendingFileDropStore";

/** Deliver only after the canonical destination composer has mounted. Promoting drafts cannot consume files. */
export function usePendingSidebarFileDrops(
  scopeKey: string | null,
  target: ScopedThreadRef | string | null,
  ready: boolean,
  composer: RefObject<ChatComposerHandle | null>,
) {
  const pending = useSidebarPendingFileDropStore((state) => state.pending);
  useEffect(() => {
    if (
      !ready ||
      !scopeKey ||
      !target ||
      typeof target === "string" ||
      !pending.some(
        (drop) => drop.scopeKey === scopeKey && isSameSidebarThreadRef(target, drop.threadRef),
      )
    )
      return;
    let frame: number | null = null;
    let cancelled = false;
    const deliver = () => {
      if (cancelled) return;
      if (!composer.current) {
        frame = requestAnimationFrame(deliver);
        return;
      }
      const files = useSidebarPendingFileDropStore
        .getState()
        .consumePendingFileDrop(scopeKey, target);
      if (files) composer.current.addDroppedFiles(files);
    };
    deliver();
    return () => {
      cancelled = true;
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [scopeKey, target, ready, composer, pending]);
}
